import { ReactNode } from "react";
import { Exercise, ExerciseDetails, MuscleGroupWeight } from "../api/types";
import { getMuscleGroupIcon } from "./muscleGroupIcons.ts";

export interface FetchedExercise {
  exercise?: Exercise;
  external: boolean;
}

export const getExerciseName = (
  exerciseId: string,
  availableExercises: Map<string, FetchedExercise>,
): ReactNode => {
  const fetched = availableExercises.get(exerciseId);
  return (
    fetched?.exercise?.name ?? <span className="italic">Unknown Exercise</span>
  );
};

export const getExerciseMuscleGroups = (
  exerciseId?: string,
  availableExercises?: Map<string, FetchedExercise>,
  exercise?: ExerciseDetails,
): MuscleGroupWeight[] => {
  if (availableExercises && exerciseId) {
    const fetched = availableExercises.get(exerciseId);
    return fetched?.exercise ? fetched.exercise.primary_muscle_groups : [];
  } else if (exercise) {
    return exercise.primary_muscle_groups;
  }

  return [];
};

export const ExerciseIcon = ({
  exerciseId,
  availableExercises,
  exercise,
}: {
  exerciseId?: string;
  availableExercises?: Map<string, FetchedExercise>;
  exercise?: ExerciseDetails;
}) => {
  const muscleGroups = getExerciseMuscleGroups(
    exerciseId,
    availableExercises,
    exercise,
  );
  const Icon = getMuscleGroupIcon(
    muscleGroups.map((muscle) => muscle.muscle_group),
  );
  return <Icon className="text-gray-600" size={20} />;
};

export const getAverageStats = (exercise: {
  sets: { resistance: number; repetitions: number }[];
  start_resistance: number;
  efficiency_factor: number;
  resistance_unit: "kg" | "lbs";
}) => {
  if (exercise.sets.length === 0) return null;

  if (exercise.sets.length === 1) {
    const totalResistance = Math.round(
      exercise.start_resistance +
        exercise.efficiency_factor * exercise.sets[0].resistance,
    );
    return `1 total set ${exercise.sets[0].repetitions} × ${totalResistance}${exercise.resistance_unit}`;
  }

  const avgReps = Math.round(
    exercise.sets.reduce((sum, s) => sum + s.repetitions, 0) /
      exercise.sets.length,
  );

  const avgAddedResistance =
    exercise.sets.reduce((sum, s) => sum + s.resistance, 0) /
    exercise.sets.length;

  const avgResistance = Math.round(
    exercise.start_resistance + exercise.efficiency_factor * avgAddedResistance,
  );

  return `${exercise.sets.length} total sets, average ${avgReps} × ${avgResistance}${exercise.resistance_unit}`;
};

export const convertResistance = (
  value: number,
  fromUnit: string,
  toUnit: string,
): number => {
  if (fromUnit === toUnit) return value;
  if (fromUnit === "kg" && toUnit === "lbs") return value * 2.20462;
  if (fromUnit === "lbs" && toUnit === "kg") return value / 2.20462;
  return value;
};

export function totalPowerPoints(exercise: ExerciseDetails) {
  return exercise.sets.length > 0
    ? exercise.sets.reduce(
        (sum, set) =>
          sum +
          set.repetitions *
            Math.pow(
              convertResistance(set.resistance, exercise.resistance_unit, "kg"),
              1.2,
            ),
        0,
      )
    : 0;
}
